import { MinusCircleTwoTone, PlusCircleTwoTone } from "@ant-design/icons";
import {
  Select as AntdSelect,
  Collapse,
  Form,
  FormListFieldData,
  InputNumber,
  Radio,
  RadioChangeEvent,
} from "antd";
import CollapsePanel from "antd/es/collapse/CollapsePanel";
import { useForm } from "antd/es/form/Form";
import Search from "antd/es/input/Search";
import { DefaultOptionType } from "antd/es/select";
import to from "await-to-js";
import dayjs from "dayjs";
import { omit } from "lodash";
import { FC, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/Button/Button";
import { RangePicker } from "../../../../components/DatePicker/DatePicker";
import { Input } from "../../../../components/Input/Input";
import { MandatoryField } from "../../../../components/MandatoryField/MandatoryField";
import { Select } from "../../../../components/Select/Select";
import { ModalsContext } from "../../../../context/modals.context";
import { DATE_FORMAT } from "../../../../core/constants";
import { ExciseStampsContext } from "../../../../modules/obligee/exciseStamps/exciseStamps.context";
import { exciseStampsService } from "../../../../modules/obligee/exciseStamps/exciseStamps.service";
import { AkciznaMarkicaKategorijaProzivoda } from "../../../../modules/obligee/exciseStamps/exciseStamps.types";
import { RecordsContext } from "../../../../modules/obligee/records/records.context";
import { recordsService } from "../../../../modules/obligee/records/records.service";
import { usedStampsService } from "../../../../modules/obligee/usedStamps/usedStamps.service";
import {
  IEvidentiranjeMarkicaPayload,
  IIskorisceneMarkicePayload,
  INeiskorisceneMarkicePayload,
} from "../../../../modules/obligee/usedStamps/usedStamps.types";
import {
  checkIfObjectFieldsAreEmpty,
  clearDoubleSpaceDot,
  clearSpecialCharacters,
  handlePaste,
} from "../../../../utils/functions";
import { useListaKategorijaZaZahtev } from "../../../../utils/hooks/useListaKategorijaZaZahtev";
import { useMarkicaZahtevIsporuceniOpsezi } from "../../../../utils/hooks/useMarkicaZahtevIsporuceniOpsezi";
import { mapSelectOptions } from "../../../../utils/map4select";
import { toastService } from "../../../../utils/toast.service";
import style from "./UsedStampsRecord.module.scss";
import ZAMBrojZahtevaGroup from "./ZAMBrojZahtevaGroup/ZAMBrojZahtevaGroup";

const FORM_INITIAL_VALUES = () => ({
  neiskorisceneMarkice: [{}],
  iskorisceneMarkice: [{}],
});

export interface ICollapseHeaderProsp {
  fields: FormListFieldData[];
  i: number;
  add: (index?: number) => void;
  remove: (index: number) => void;
}

const CollapseHeader: FC<ICollapseHeaderProsp> = ({
  i,
  fields,
  add,
  remove,
}) => {
  const { t } = useTranslation();

  return (
    <div className={style.collapseHeader}>
      <div>{t("EVIDENCIJA_MARKICA.GRUPA_MARKICA_BROJ", { broj: i + 1 })}</div>
      <div>
        {i === fields.length - 1 && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              add();
            }}
          >
            <Form.Item>
              <PlusCircleTwoTone />
            </Form.Item>
          </div>
        )}
        {i !== fields.length - 1 && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              remove(i);
            }}
          >
            <Form.Item>
              <MinusCircleTwoTone twoToneColor={"red"} />
            </Form.Item>
          </div>
        )}
      </div>
    </div>
  );
};

export const UsedStampsRecord = () => {
  const { exciseStamps, setExciseStamps } = useContext(ExciseStampsContext);
  const { records, setRecords } = useContext(RecordsContext);
  const { setError } = useContext(ModalsContext);
  const [masineOptions, setMasineOptions] = useState<DefaultOptionType[]>([]);
  const [formType, setFormType] =
    useState<AkciznaMarkicaKategorijaProzivoda | null>(null);
  const [tobaccoType, setTobaccoType] = useState(null);
  const [form] = useForm();
  const { t } = useTranslation();
  const [periodi, setPeriodi] = useState<Array<DefaultOptionType>>([]);
  const [isLoadingPeriodi, setIsLoadingPeriodi] = useState(false);
  const [tipoviPakovanja, setTipoviPakovanja] = useState<
    Array<Omit<DefaultOptionType, "children">>
  >([]);

  const opsegIdValue = Form.useWatch(
    ["osteceneMarkiceSaVidljivomSerijom", "isporucenOpsegId"],
    form
  );

  // Watch values for conditional labels
  const opsegOdOsteceneValue = Form.useWatch(
    ["osteceneMarkiceSaVidljivomSerijom", "opsegOdOstecene"],
    form
  );
  const opsegDoOsteceneValue = Form.useWatch(
    ["osteceneMarkiceSaVidljivomSerijom", "opsegDoOstecene"],
    form
  );
  const osteceneValue = Form.useWatch(
    ["osteceneMarkiceSaVidljivomSerijom", "ostecene"],
    form
  );

  const hasOpsegOdOstecene =
    opsegOdOsteceneValue !== undefined &&
    opsegOdOsteceneValue !== null &&
    opsegOdOsteceneValue !== "";

  const hasOpsegDoOstecene =
    opsegDoOsteceneValue !== undefined &&
    opsegDoOsteceneValue !== null &&
    opsegDoOsteceneValue !== "";

  const hasOstecene =
    osteceneValue && Array.isArray(osteceneValue) && osteceneValue.length > 0;
  const hasOpsegOdDoOstecene = hasOpsegOdOstecene || hasOpsegDoOstecene;
  const hasBothOpsegAndOstecene = hasOpsegOdDoOstecene && hasOstecene;

  // Conditions for mandatory fields:
  // 1. Initially all empty → all MandatoryField
  // 2. User fills opseg od/do → opseg od/do keep MandatoryField, pojedinacni gets regular
  // 3. User fills pojedinacni and opseg od/do are empty → opseg od/do get regular, pojedinacni keeps MandatoryField
  // 4. User fills opseg od/do AND pojedinacni → all three MandatoryField
  const opsegOdOsteceneLabel =
    hasBothOpsegAndOstecene ||
    (!hasOstecene && hasOpsegOdDoOstecene) ||
    (!hasOpsegOdDoOstecene && !hasOstecene)
      ? MandatoryField("EVIDENCIJA_MARKICA.OPSEG_OD")
      : t("EVIDENCIJA_MARKICA.OPSEG_OD");

  const opsegDoOsteceneLabel =
    hasBothOpsegAndOstecene ||
    (!hasOstecene && hasOpsegOdDoOstecene) ||
    (!hasOpsegOdDoOstecene && !hasOstecene)
      ? MandatoryField("EVIDENCIJA_MARKICA.OPSEG_DO")
      : t("EVIDENCIJA_MARKICA.OPSEG_DO");

  const osteceneLabel =
    hasBothOpsegAndOstecene ||
    (!hasOpsegOdDoOstecene && hasOstecene) ||
    (!hasOpsegOdDoOstecene && !hasOstecene)
      ? MandatoryField("EVIDENCIJA_MARKICA.POJEDINACNI_UNOS")
      : t("EVIDENCIJA_MARKICA.POJEDINACNI_UNOS");

  const {
    brojZahtevaOpsegError,
    handleBrojZahtevaOpsegChange,
    handleVrstaKategorijeOpsegChange,
    isLoadingOpsegKategorije,
    isOpsegKategorijeDisabled,
    opsegKategorije,
    opsezi,
    resetOpsegKategorije,
  } = useMarkicaZahtevIsporuceniOpsezi({
    form,
    zahtevFieldName: ["osteceneMarkiceSaVidljivomSerijom", "zahtevOpsegId"],
    kategorijaFieldName: [
      "osteceneMarkiceSaVidljivomSerijom",
      "markicaKategorijeVrsteId",
    ],
    markicaKategorijeId: formType,
    onKategorijaChange: clearOpsegFields,
  });

  const {
    isLoadingKategorije,
    kategorije,
    isKategorijeDisabled,
    brojZahtevaError,
    handleBrojZahtevaChange,
    resetKategorije,
  } = useListaKategorijaZaZahtev({
    form,
    zahtevFieldName: ["osteceneMarkiceSaNevidljivomSerijom", "zahtevId"],
    kategorijaFieldName: [
      "osteceneMarkiceSaNevidljivomSerijom",
      "markicaKategorijeVrsteId",
    ],
    markicaKategorijeId: formType,
  });

  function handleSetOpsegFields(
    opseg: "od" | "do",
    value: string | number | null
  ) {
    if (opseg === "od") {
      form.setFieldValue(
        ["osteceneMarkiceSaVidljivomSerijom", "opsegOdOstecene"],
        value
      );
    } else {
      form.setFieldValue(
        ["osteceneMarkiceSaVidljivomSerijom", "opsegDoOstecene"],
        value
      );
    }
  }

  function clearOpsegFields(isOpseg: boolean = false) {
    if (!isOpseg) {
      form.setFieldValue(
        ["osteceneMarkiceSaVidljivomSerijom", "isporucenOpsegId"],
        null
      );
    }
    form.setFieldValue(
      ["osteceneMarkiceSaVidljivomSerijom", "opsegOdOstecene"],
      null
    );
    form.setFieldValue(
      ["osteceneMarkiceSaVidljivomSerijom", "opsegDoOstecene"],
      null
    );
    form.setFieldValue(["osteceneMarkiceSaVidljivomSerijom", "ostecene"], []);
  }

  const handleVrstaKategorijeOsteceneNevidljiveChange = async (
    value: number,
    option?: DefaultOptionType | DefaultOptionType[]
  ) => {
    if (Array.isArray(option)) {
      return;
    }

    form.setFieldValue(
      ["osteceneMarkiceSaNevidljivomSerijom", "markicaKategorijeVrsteId"],
      value
    );

    if (formType === AkciznaMarkicaKategorijaProzivoda.KAFA) {
      form.setFieldValue(
        ["osteceneMarkiceSaNevidljivomSerijom", "tipPakovanjaId"],
        null
      );

      setTipoviPakovanja(option?.children ?? []);
    }
  };

  const datumProizvodnjeLabel = useMemo(() => {
    switch (formType) {
      case AkciznaMarkicaKategorijaProzivoda.KAFA:
        return MandatoryField(
          "PLACEHOLDERS.DATUM_PROIZVODNJE_PAKOVANJA_ILI_DATUM_CARINJENJA"
        );
      case AkciznaMarkicaKategorijaProzivoda.ALKOHOL:
        return MandatoryField(
          "PLACEHOLDERS.DATUM_PROIZVODNJE_PUNJENJA_ILI_DATUM_CARINJENJA"
        );
      case AkciznaMarkicaKategorijaProzivoda.DUVAN:
      default:
        return MandatoryField("PLACEHOLDERS.DATUM_PROIZVODNJE");
    }
  }, [formType]);

  const getInicijalniPodaci = async () => {
    const [err, res] = await to(
      exciseStampsService.getInicijalniPodaciZaZahtevObveznika()
    );
    if (err || !res.success) return setError(res?.errorMessage);
    setExciseStamps({
      kategorijaProizvoda: res.data.kategorijaProizvoda,
      vrstaProizvoda: res.data.vrstaProizvoda,
    });
    setFormType(res.data.kategorijaProizvoda[0]?.idKategorijeProizvoda);
  };

  // kafa and alkohol only
  const showAmbalazaInput = [1, 3].includes(formType as number);
  const isKafaOrAlkohol = [1, 3].includes(formType as number);
  const isAlkohol = formType === AkciznaMarkicaKategorijaProzivoda.ALKOHOL;

  const onSubmit = async (values: IEvidentiranjeMarkicaPayload) => {
    const allIskorisceneMarkiceEmpty = values.iskorisceneMarkice?.every(
      (markica) => checkIfObjectFieldsAreEmpty(markica)
    );
    const allNeiskorisceneMarkiceEmpty = values.neiskorisceneMarkice?.every(
      (markica) => checkIfObjectFieldsAreEmpty(markica)
    );
    const isAlkohol = formType === AkciznaMarkicaKategorijaProzivoda.ALKOHOL;

    const iskorisceneMarkice = allIskorisceneMarkiceEmpty
      ? []
      : values.iskorisceneMarkice?.map(
          (markica: IIskorisceneMarkicePayload | undefined) => ({
            ...omit(
              markica,
              "kategorija",
              "zahtev",
              isAlkohol ? "kolicina" : ""
            ),
            ...(isAlkohol
              ? {
                  opsegOdIskoriscene: markica?.opsegOdIskoriscene,
                  opsegDoIskoriscene: markica?.opsegDoIskoriscene,
                }
              : {}),
            datumProizvodnje:
              markica?.datumProizvodnje?.[0] && markica?.datumProizvodnje?.[1]
                ? [
                    dayjs(markica?.datumProizvodnje?.[0]).format(
                      "YYYY-MM-DDT00:00:00"
                    ),
                    dayjs(markica?.datumProizvodnje?.[1]).format(
                      "YYYY-MM-DDT00:00:00"
                    ),
                  ]
                : [],
          })
        );

    const neiskorisceneMarkice = allNeiskorisceneMarkiceEmpty
      ? []
      : values.neiskorisceneMarkice?.map(
          (markica: INeiskorisceneMarkicePayload | undefined) => ({
            ...omit(
              markica,
              "kategorija",
              "zahtev",
              isAlkohol ? "kolicina" : ""
            ),
            ...(isAlkohol
              ? {
                  opsegOdNeiskoriscene: markica?.opsegOdNeiskoriscene,
                  opsegDoNeiskoriscene: markica?.opsegDoNeiskoriscene,
                  neiskoriscene: markica?.neiskoriscene,
                }
              : {}),
          })
        );

    const [err, res] = await to(
      usedStampsService.createEvidentiranjeMarkica({
        ...values,
        osteceneMarkiceSaVidljivomSerijom: {
          ...(isAlkohol
            ? {
                ostecene: values?.osteceneMarkiceSaVidljivomSerijom?.ostecene,
                opsegOdOstecene:
                  values?.osteceneMarkiceSaVidljivomSerijom?.opsegOdOstecene,
                opsegDoOstecene:
                  values?.osteceneMarkiceSaVidljivomSerijom?.opsegDoOstecene,
              }
            : {}),
          ...(!isAlkohol
            ? { kolicina: values?.osteceneMarkiceSaVidljivomSerijom?.kolicina }
            : {}),
          periodId: values?.osteceneMarkiceSaVidljivomSerijom?.periodId,
          isporucenOpsegId:
            values?.osteceneMarkiceSaVidljivomSerijom?.isporucenOpsegId,
        },
        iskorisceneMarkice,
        neiskorisceneMarkice,
        kategorijaProizvoda: formType as number,
      })
    );

    if (err || !res.success) return setError(res?.errorMessage);
    toastService.success("TOAST.EVIDENTIRANJE_MARKICA_SUCCESS");
    form.resetFields();
  };

  const createDrustvoAktivniObjekti = async () => {
    const [err, res] = await to(recordsService.createDrustvoAktivniObjekti());
    if (err || !res.success) return setError(res?.errorMessage);
    setRecords({ drustvoAktivniObjekti: res.data });
  };

  const createRobneMarkePretraga = async (
    pretraga: string | null,
    vrstaProizvodaId: number | null
  ) => {
    const [err, res] = await to(
      recordsService.getAllTrademarks({
        naziv: pretraga,
        vrstaProizvodaId: vrstaProizvodaId,
      })
    );
    if (err || !res.success) return setError(res?.errorMessage);
    setRecords({ robneMarkePretraga: res.data });
  };

  const options = recordsService.getDrustvoAktivniObjektiOptions(
    records.drustvoAktivniObjekti
  );

  const handleChangeObjekat = (id: number, field: number) => {
    const selectedObj = records.drustvoAktivniObjekti.find(
      (aktivniObj) => aktivniObj.id === id
    );
    if (selectedObj) {
      setMasineOptions(
        mapSelectOptions({
          sourceArray: selectedObj.masine,
          valueAccessor: "id",
          labelAccessor: "serijskiBroj",
        })
      );
    }

    form.resetFields([["iskorisceneMarkice", field, "objekatMasinaId"]]);
  };

  const handleChangeFormType = (e: RadioChangeEvent) => {
    setFormType(e.target.value);
    form.resetFields();
  };

  const handleBrojZahtevaSubmit = async (brojZahteva: string) => {
    form.setFieldValue(
      ["osteceneMarkiceSaNevidljivomSerijom", "markicaKategorijeVrsteId"],
      null
    );

    if (formType === AkciznaMarkicaKategorijaProzivoda.KAFA) {
      form.setFieldValue(
        ["osteceneMarkiceSaNevidljivomSerijom", "tipPakovanjaId"],
        null
      );
      setTipoviPakovanja([]);
    }

    resetKategorije();

    try {
      const markicaKategorijaVrsta = await handleBrojZahtevaChange(brojZahteva);

      if (markicaKategorijaVrsta) {
        const tipoviPakovanja =
          markicaKategorijaVrsta.tipoviPakovanjaZaVrstuKategorije?.map(
            (tip) => ({
              value: tip.tipPakovanjaId,
              label: tip.tipPakovanjaNaziv,
            })
          ) ?? [];

        setTipoviPakovanja(tipoviPakovanja);
      }
    } catch (err) {}
  };

  const handleBrojZahtevaOpsegSubmit = (brojZahteva: string) => {
    form.setFieldValue(
      ["osteceneMarkiceSaVidljivomSerijom", "markicaKategorijeVrsteId"],
      null
    );

    form.setFieldValue(
      ["osteceneMarkiceSaVidljivomSerijom", "isporucenOpsegId"],
      null
    );

    resetOpsegKategorije();

    try {
      handleBrojZahtevaOpsegChange(brojZahteva);
    } catch (err) {}
  };

  const fetchPeriods = async () => {
    try {
      setIsLoadingPeriodi(true);

      const response =
        await exciseStampsService.getEvidencijaPeriodicniIzvestaji();

      setPeriodi(
        response.data
          ? response.data?.map((period) => ({
              value: period.periodId,
              label: period.period,
            }))
          : []
      );
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingPeriodi(false);
    }
  };

  const handleIsporucenOpsegChange = (value: number) => {
    form.setFieldValue(
      ["osteceneMarkiceSaVidljivomSerijom", "isporucenOpsegId"],
      value
    );

    clearOpsegFields(true);
  };

  const handleOsteceneChange = (value: string[] | string | undefined) => {
    if (!value || !Array.isArray(value)) return;

    const newValue = value.reduce<string[]>((acc, v) => {
      if (typeof v !== "string") return acc;

      const sanitized = v.replace(/\./g, "").replace(/ /g, "").trim();

      if (sanitized) {
        acc.push(sanitized);
      }
      return acc;
    }, []);

    form.setFieldValue(
      ["osteceneMarkiceSaVidljivomSerijom", "ostecene"],
      newValue
    );
  };

  useEffect(() => {
    form.resetFields(["robnaMarkaId"]);
  }, [tobaccoType]);

  useEffect(() => {
    createDrustvoAktivniObjekti();
  }, []);

  useEffect(() => {
    getInicijalniPodaci();
  }, []);

  useEffect(() => {
    fetchPeriods();
  }, []);

  return (
    <div className={style.mainContainer}>
      <Form
        form={form}
        labelCol={{ span: 14 }}
        labelAlign="left"
        wrapperCol={{ span: 14 }}
        initialValues={FORM_INITIAL_VALUES()}
        className={style.formWrapper}
        onFinish={(values: IEvidentiranjeMarkicaPayload) => {
          onSubmit(values);
        }}
      >
        <div className={style.form}>
          <div className={`${style.subContainer} ${style.radioWrapper}`}>
            <span>{MandatoryField("TABLES.VRSTA_PROIZVODA")}:</span>
            <Radio.Group onChange={handleChangeFormType} value={formType}>
              {exciseStamps.kategorijaProizvoda.map((kategorija) => (
                <Radio
                  key={kategorija.idKategorijeProizvoda}
                  value={kategorija.idKategorijeProizvoda}
                >
                  {kategorija.imeKategorijeProizvoda}
                </Radio>
              ))}
            </Radio.Group>
          </div>
          <div className={style.subContainer}>
            <div className="w-45">
              <h3 className={style.title}>
                {t("EVIDENCIJA_MARKICA.OSTECENE_MARKICE_VIDLJIVE")}
              </h3>
              <Form.Item
                name={["osteceneMarkiceSaVidljivomSerijom", "periodId"]}
                label={MandatoryField("AKCIZNE_MARKICE.PERIOD")}
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 14 }}
                className="mb-20"
                colon={false}
              >
                <AntdSelect
                  placeholder={t("AKCIZNE_MARKICE.PERIOD")}
                  options={periodi}
                  loading={isLoadingPeriodi}
                />
              </Form.Item>
              <Form.Item
                className="mb-20"
                name={["osteceneMarkiceSaVidljivomSerijom", "zahtevOpsegId"]}
                colon={false}
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 14 }}
                label={MandatoryField("EVIDENCIJA_MARKICA.ZAM_ID_ZAHTEVA")}
                rules={[
                  {
                    validator: (_, value) => {
                      if (
                        !!value &&
                        brojZahtevaOpsegError?.brojZahteva === value
                      ) {
                        return Promise.reject();
                      }
                      return Promise.resolve();
                    },
                    message: brojZahtevaOpsegError?.message ?? "",
                  },
                ]}
              >
                <Search
                  placeholder={t("EVIDENCIJA_MARKICA.ZAM_ID_ZAHTEVA")}
                  name="zahtevOpsegId"
                  enterButton={t("COMMON.POTVRDI")}
                  onSearch={(brojZahteva) =>
                    handleBrojZahtevaOpsegSubmit(brojZahteva)
                  }
                  // Needed to prevent form from submitting
                  onKeyDown={(e) =>
                    e.key == "Enter" ? e.preventDefault() : ""
                  }
                  allowClear
                />
              </Form.Item>
              <Form.Item
                name={[
                  "osteceneMarkiceSaVidljivomSerijom",
                  "markicaKategorijeVrsteId",
                ]}
                label={MandatoryField("TABLES.VRSTA_KATEGORIJE")}
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 14 }}
                className="mb-20"
                colon={false}
              >
                <AntdSelect
                  placeholder={t("TABLES.VRSTA_KATEGORIJE")}
                  options={opsegKategorije}
                  loading={isLoadingOpsegKategorije}
                  disabled={isOpsegKategorijeDisabled}
                  onChange={handleVrstaKategorijeOpsegChange}
                />
              </Form.Item>
              <Form.Item
                name={["osteceneMarkiceSaVidljivomSerijom", "isporucenOpsegId"]}
                label={MandatoryField("TABLES.OPSEG")}
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 14 }}
                className="mb-20"
                colon={false}
              >
                <AntdSelect
                  placeholder={t("TABLES.OPSEG")}
                  options={opsezi}
                  loading={isLoadingOpsegKategorije}
                  onChange={handleIsporucenOpsegChange}
                  disabled={isOpsegKategorijeDisabled}
                />
              </Form.Item>
              {!isAlkohol && (
                <Form.Item
                  className="mb-20"
                  name={["osteceneMarkiceSaVidljivomSerijom", "kolicina"]}
                  colon={false}
                  labelCol={{ span: 6 }}
                  wrapperCol={{ span: 14 }}
                  label={MandatoryField("COMMON.KOLICINA")}
                >
                  <InputNumber
                    placeholder={t("COMMON.KOLICINA")}
                    style={{ width: "100%" }}
                    min={0}
                    name="kolicina-ostecene"
                  />
                </Form.Item>
              )}
              {isAlkohol && (
                <>
                  <Form.Item
                    name={[
                      "osteceneMarkiceSaVidljivomSerijom",
                      "opsegOdOstecene",
                    ]}
                    label={opsegOdOsteceneLabel}
                    colon={false}
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 14 }}
                    className="mb-20"
                  >
                    <Input
                      name="opsegOdOstecene"
                      placeholder={t("EVIDENCIJA_MARKICA.OPSEG_OD")}
                      disabled={!opsegIdValue}
                      onKeyDown={clearSpecialCharacters}
                      onPaste={handlePaste}
                      onChange={(e) => {
                        clearDoubleSpaceDot(e);

                        handleSetOpsegFields("od", e.target.value);
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    name={[
                      "osteceneMarkiceSaVidljivomSerijom",
                      "opsegDoOstecene",
                    ]}
                    label={opsegDoOsteceneLabel}
                    colon={false}
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 14 }}
                    className="mb-20"
                  >
                    <Input
                      name="opsegDoOstecene"
                      placeholder={t("EVIDENCIJA_MARKICA.OPSEG_DO")}
                      disabled={!opsegIdValue}
                      onKeyDown={clearSpecialCharacters}
                      onPaste={handlePaste}
                      onChange={(e) => {
                        clearDoubleSpaceDot(e);

                        handleSetOpsegFields("do", e.target.value);
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 14 }}
                    name={["osteceneMarkiceSaVidljivomSerijom", "ostecene"]}
                    label={osteceneLabel}
                    colon={false}
                    className="mb-20"
                    tooltip={t("EVIDENCIJA_MARKICA.POJEDINACNI_UNOS_TOOLTIP", {
                      ENTER: "Enter",
                    })}
                  >
                    <AntdSelect
                      mode="tags"
                      placeholder={t("EVIDENCIJA_MARKICA.POJEDINACNI_UNOS")}
                      tokenSeparators={[","]}
                      style={{ width: "100%" }}
                      open={false}
                      suffixIcon={null}
                      onInputKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                        }
                      }}
                      onChange={handleOsteceneChange}
                      onKeyDown={(e) =>
                        clearSpecialCharacters(
                          e as React.KeyboardEvent<HTMLInputElement>,
                          [","]
                        )
                      }
                    />
                  </Form.Item>
                </>
              )}
            </div>
            <div className="w-40">
              <h3 className={style.title}>
                {t("EVIDENCIJA_MARKICA.OSTECENE_MARKICE_NEVIDLJIVE")}
              </h3>
              <Form.Item
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 14 }}
                name={["osteceneMarkiceSaNevidljivomSerijom", "periodId"]}
                label={MandatoryField("AKCIZNE_MARKICE.PERIOD")}
                colon={false}
                className="mb-20"
              >
                <AntdSelect
                  placeholder={t("AKCIZNE_MARKICE.PERIOD")}
                  options={periodi}
                  loading={isLoadingPeriodi}
                />
              </Form.Item>
              <Form.Item
                className="mb-20"
                name={["osteceneMarkiceSaNevidljivomSerijom", "zahtevId"]}
                colon={false}
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 14 }}
                label={MandatoryField("EVIDENCIJA_MARKICA.ZAM_ID_ZAHTEVA")}
                rules={[
                  {
                    validator: (_, value) => {
                      if (!!value && brojZahtevaError?.brojZahteva === value) {
                        return Promise.reject();
                      }
                      return Promise.resolve();
                    },
                    message: brojZahtevaError?.message ?? "",
                  },
                ]}
              >
                <Search
                  placeholder={t("EVIDENCIJA_MARKICA.ZAM_ID_ZAHTEVA")}
                  name="zahtevId"
                  enterButton={t("COMMON.POTVRDI")}
                  onSearch={(brojZahteva) =>
                    handleBrojZahtevaSubmit(brojZahteva)
                  }
                  // Needed to prevent form from submitting
                  onKeyDown={(e) =>
                    e.key == "Enter" ? e.preventDefault() : ""
                  }
                  allowClear
                />
              </Form.Item>
              <Form.Item
                className="mb-20"
                name={[
                  "osteceneMarkiceSaNevidljivomSerijom",
                  "markicaKategorijeVrsteId",
                ]}
                label={MandatoryField("TABLES.VRSTA_KATEGORIJE")}
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 14 }}
                colon={false}
              >
                <AntdSelect
                  placeholder={t("TABLES.VRSTA_KATEGORIJE")}
                  options={kategorije}
                  loading={isLoadingKategorije}
                  disabled={isKategorijeDisabled}
                  onChange={handleVrstaKategorijeOsteceneNevidljiveChange}
                />
              </Form.Item>
              {formType === AkciznaMarkicaKategorijaProzivoda.KAFA ? (
                <Form.Item
                  className="mb-20"
                  name={[
                    "osteceneMarkiceSaNevidljivomSerijom",
                    "tipPakovanjaId",
                  ]}
                  label={MandatoryField("TRACK.TIP_PAKOVANJA")}
                  labelCol={{ span: 6 }}
                  wrapperCol={{ span: 14 }}
                  colon={false}
                >
                  <AntdSelect
                    placeholder={t("TRACK.TIP_PAKOVANJA")}
                    options={tipoviPakovanja}
                    loading={isLoadingKategorije}
                    disabled={isKategorijeDisabled}
                  />
                </Form.Item>
              ) : (
                <></>
              )}
              <Form.Item
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 14 }}
                name={["osteceneMarkiceSaNevidljivomSerijom", "kolicina"]}
                colon={false}
                label={MandatoryField("COMMON.KOLICINA")}
              >
                <InputNumber
                  placeholder={t("COMMON.KOLICINA")}
                  min={0}
                  style={{ width: "100%" }}
                  name="kolicina-nevidljivih"
                />
              </Form.Item>
            </div>
          </div>
          <div className={style.subContainer}>
            <div className="w-45">
              <h3 className={style.title}>
                {t("EVIDENCIJA_MARKICA.ISKORISCENE_MARKICE")}
              </h3>
              <Form.List name="iskorisceneMarkice">
                {(fields, { add, remove }) => (
                  <Collapse accordion>
                    {fields.map((field, i) => {
                      return (
                        <CollapsePanel
                          key={field.key}
                          header={
                            <CollapseHeader
                              i={i}
                              fields={fields}
                              add={add}
                              remove={remove}
                            />
                          }
                        >
                          <>
                            <ZAMBrojZahtevaGroup
                              form={form}
                              index={field.name}
                              key={field.key}
                              kategorijaFieldName={[field.name, "kategorija"]}
                              zahtevFieldName={[field.name, "zahtev"]}
                              opsegFieldName={[field.name, "isporucenOpsegId"]}
                              kolicinaFieldName={[field.name, "kolicina"]}
                              ambalazaFieldName={[field.name, "ambalaza"]}
                              showAmbalazaInput={isKafaOrAlkohol}
                              listFieldName="iskorisceneMarkice"
                              markicaKategorijeId={formType}
                              showKolicinaInput={!isAlkohol}
                              opsegOdFieldName={[
                                field.name,
                                "opsegOdIskoriscene",
                              ]}
                              opsegDoFieldName={[
                                field.name,
                                "opsegDoIskoriscene",
                              ]}
                              showOpsegOdDo={isAlkohol}
                            />
                            <Form.Item
                              name={[field.name, "datumProizvodnje"]}
                              colon={false}
                              label={datumProizvodnjeLabel}
                            >
                              <RangePicker
                                className="mb-30"
                                placeholder={[
                                  t("PLACEHOLDERS.OD"),
                                  t("PLACEHOLDERS.DO"),
                                ]}
                                format={[DATE_FORMAT]}
                                name="datum-proizvodnje-evidencija-markica"
                              />
                            </Form.Item>
                            <Form.Item
                              name={[field.name, "objekatId"]}
                              colon={false}
                              label={MandatoryField(
                                "EVIDENCIJA_MARKICA.LOKACIJA"
                              )}
                            >
                              <Select
                                options={options}
                                onChange={(value) =>
                                  handleChangeObjekat(value, field.name)
                                }
                                className="mb-25"
                                name="lokacija"
                                placeholder={t("PLACEHOLDERS.IZABERI_LOKACIJU")}
                              />
                            </Form.Item>
                            <Form.Item
                              name={[field.name, "objekatMasinaId"]}
                              colon={false}
                              label={t("EVIDENCIJA_MARKICA.MASINA")}
                            >
                              <Select
                                className="mb-25"
                                placeholder={t("PLACEHOLDERS.IZABERI_MASINU")}
                                options={masineOptions}
                                allowClear
                                filterOption={false}
                                showSearch
                                name="masine"
                              />
                            </Form.Item>
                            {!showAmbalazaInput && (
                              <>
                                <Form.Item
                                  className="mb-25"
                                  colon={false}
                                  name={[field.name, "duvanskiProizvodVrstaId"]}
                                  label={MandatoryField(
                                    "QR_CODE.VRSTA_DUVANSKOG_PROIZVODA"
                                  )}
                                >
                                  <Radio.Group
                                    onChange={({ target }) => {
                                      form.setFieldValue(
                                        [
                                          "iskorisceneMarkice",
                                          field.name,
                                          "robnaMarkaId",
                                        ],
                                        null
                                      );
                                      setTobaccoType(target.value);
                                      createRobneMarkePretraga(
                                        null,
                                        target.value
                                      );
                                    }}
                                    options={
                                      mapSelectOptions({
                                        sourceArray:
                                          exciseStamps.vrstaProizvoda,
                                        valueAccessor: "id",
                                        labelAccessor: "naziv",
                                      }) as any
                                    }
                                  />
                                </Form.Item>
                                <Form.Item
                                  name={[field.name, "robnaMarkaId"]}
                                  colon={false}
                                  className="mb-25"
                                  label={MandatoryField(
                                    "QR_CODE.ROBNA_MARKA_DUVANSOG_PROIZVODA"
                                  )}
                                >
                                  {tobaccoType && (
                                    <Select
                                      style={{ width: "100%" }}
                                      options={
                                        mapSelectOptions({
                                          sourceArray:
                                            records.robneMarkePretraga,
                                          valueAccessor: "id",
                                          labelAccessor: "naziv",
                                        }) || []
                                      }
                                      allowClear
                                      placeholder={t(
                                        "PLACEHOLDERS.IZABERI_BREND"
                                      )}
                                      filterOption={(input, option) => {
                                        const label: any = option?.label;
                                        if (label) {
                                          return (
                                            label
                                              .toLowerCase()
                                              .indexOf(input.toLowerCase()) >= 0
                                          );
                                        }
                                        return [] as any;
                                      }}
                                      showSearch
                                      name="brend"
                                    />
                                  )}
                                </Form.Item>
                              </>
                            )}
                            {showAmbalazaInput && (
                              <Form.Item
                                name={[field.name, "robnaMarkaNaziv"]}
                                colon={false}
                                label={MandatoryField(
                                  "EVIDENCIJA_MARKICA.BREND"
                                )}
                              >
                                <Input
                                  className="mb-25"
                                  placeholder={t("EVIDENCIJA_MARKICA.BREND")}
                                  name="brend-naziv"
                                />
                              </Form.Item>
                            )}
                          </>
                        </CollapsePanel>
                      );
                    })}
                  </Collapse>
                )}
              </Form.List>
            </div>
            <div className="w-40">
              <h3 className={style.neiskorisceneTitle}>
                {t("EVIDENCIJA_MARKICA.NEISKORISCENE_MARKICE")}
              </h3>
              <Form.List name="neiskorisceneMarkice">
                {(fields, { add, remove }) => (
                  <Collapse accordion>
                    {fields.map((field, i) => {
                      return (
                        <CollapsePanel
                          key={field.key}
                          header={
                            <CollapseHeader
                              i={i}
                              fields={fields}
                              add={add}
                              remove={remove}
                            />
                          }
                        >
                          <div>
                            <Form.Item
                              name={[field.name, "periodId"]}
                              label={MandatoryField("AKCIZNE_MARKICE.PERIOD")}
                              colon={false}
                              className="mb-30"
                            >
                              <AntdSelect
                                placeholder={t("AKCIZNE_MARKICE.PERIOD")}
                                options={periodi}
                                loading={isLoadingPeriodi}
                              />
                            </Form.Item>
                            <ZAMBrojZahtevaGroup
                              form={form}
                              kategorijaFieldName={[field.name, "kategorija"]}
                              zahtevFieldName={[field.name, "zahtev"]}
                              opsegFieldName={[field.name, "isporucenOpsegId"]}
                              kolicinaFieldName={[field.name, "kolicina"]}
                              index={field.name}
                              listFieldName="neiskorisceneMarkice"
                              markicaKategorijeId={formType}
                              showKolicinaInput={!isAlkohol}
                              opsegOdFieldName={[
                                field.name,
                                "opsegOdNeiskoriscene",
                              ]}
                              opsegDoFieldName={[
                                field.name,
                                "opsegDoNeiskoriscene",
                              ]}
                              pojedinacniUnosFieldName={[
                                field.name,
                                "neiskoriscene",
                              ]}
                              showOpsegOdDo={isAlkohol}
                              showPojedinacniUnos={isAlkohol}
                            />
                          </div>
                        </CollapsePanel>
                      );
                    })}
                  </Collapse>
                )}
              </Form.List>
            </div>
          </div>
          <div className={style.footer}>
            <Form.Item>
              <Button htmlType="submit" type="primary">
                {t("COMMON.SACUVAJ")}
              </Button>
            </Form.Item>
          </div>
        </div>
      </Form>
    </div>
  );
};
